import { Box, Divider, Search } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ACTIONS } from '../../../../constants';
import { TLocalesKeys } from '../../../../types';
import { classNames } from '../../../../util';
import { logClearSearchAction, logSearchBarAction } from '../../../bi';
import { useCurrentPage } from '../../../common/hooks/useCurrentPage';
import { useDebounce } from '../../../common/hooks/useDebounce';
import { helpCenterSlice } from '../../../common/reducers/helpCenter';
import { solutionsSlice } from '../../../common/reducers/solutions';
import { selectArticleHeadingVisible } from '../../../common/selectors/helpCenter';
// TODO:import-chatbot-from-help-center move to common items used in chatbot and helpcenter
import { selectSearchFocus } from '../../../common/selectors/inputsFocus';
import { selectSolutions } from '../../../common/selectors/solutions';
import { Page } from '../../../common/types';
import { appRoutes } from '../../../routes';
import { renderModel } from '../../../util/renderModel';
import styles from './styles.scss';

const isLandingPage = renderModel.isLandingPage();
const debounceTime = 2000;
const resetFocusSearchAction = {
  type: ACTIONS.INPUTS_FOCUS_SEARCH,
  search: false,
};

export const SearchBar: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const store = useStore();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');
  const [searchTerm, setSearchTerm] = useState(query ?? '');
  const [searched, setSearched] = useState(false);
  const [debouncedSearchTerm, resetDebouncedSearch] = useDebounce(
    searchTerm,
    debounceTime,
  );
  const dispatch = useDispatch();
  const searchFocus = useSelector(selectSearchFocus);
  const articleHeadingVisible = useSelector(selectArticleHeadingVisible);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const solutions = useSelector(selectSolutions);
  const currentPage = useCurrentPage();
  const isHomePage = useMemo(() => currentPage === Page.Home, [currentPage]);
  const isSearchPage = useMemo(() => currentPage === Page.Search, [
    currentPage,
  ]);

  const search = useCallback(
    (searchQuery) => {
      if (searchParams.get('query') === searchQuery) {
        return;
      }

      searchParams.set('query', searchQuery);
      dispatch(helpCenterSlice.actions.setCurrentCategory(null));
      dispatch(solutionsSlice.actions.setIsUserTypedSearch(true));

      navigate(
        {
          pathname: appRoutes.search(),
          search: searchParams.toString(),
        },
        { replace: isSearchPage },
      );
      setSearched(true);
      // Timeout is needed to wait for search term update bi helpcenter
      setTimeout(() => {
        logSearchBarAction(store.getState());
      }, 1);
    },
    [store, searchParams, dispatch, navigate, isSearchPage],
  );

  const onChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => setSearchTerm(target.value),
    [setSearchTerm],
  );
  const onClear = useCallback(() => {
    setSearchTerm('');

    if (searched) {
      // TODO:bi used for other BI events that are not refactored yet
      logClearSearchAction(store.getState());
      setSearched(false);
      searchParams.set('query', '');
      navigate(-1);
    }
  }, [store, searched, solutions, searchParams, navigate]);
  const onBlur = useCallback(
    () => searchFocus && dispatch(resetFocusSearchAction),
    [dispatch, searchFocus],
  );
  const onEnter = useCallback(() => {
    resetDebouncedSearch();
    searchTerm && search(searchTerm);
  }, [resetDebouncedSearch, searchTerm, search]);

  useEffect(() => setSearchTerm(query ?? ''), [query]);
  useEffect(() => {
    debouncedSearchTerm && search(debouncedSearchTerm);
  }, [debouncedSearchTerm]);
  useEffect(() => {
    searchFocus && searchRef.current?.focus();
  }, [searchFocus]);

  if (
    isLandingPage ||
    articleHeadingVisible ||
    currentPage === Page.Chatbot ||
    currentPage === Page.RequestSuccessManagerForm
  ) {
    return null;
  }

  return (
    <>
      <Box
        width="100%"
        maxWidth="986px"
        direction="vertical"
        padding={isHomePage ? '0 18px 18px' : '18px'}
        dataHook="search-bar"
        className={classNames([
          isHomePage && styles.searchBarContainerHomePage,
        ])}
      >
        <Search
          inputRef={(inputRef) => (searchRef.current = inputRef)}
          size="large"
          border="standard"
          clearButton
          placeholder={t<TLocalesKeys>('help-center-search-placeholder-new')}
          value={searchTerm}
          onChange={onChange}
          onEnterPressed={onEnter}
          onClear={onClear}
          onBlur={onBlur}
          dataHook="search-bar__search"
        />
      </Box>
      {!isHomePage && <Divider />}
    </>
  );
};
